import React, { useCallback, useEffect, useState } from 'react';
import { Button, ConfigProvider, Drawer, Input, Tree, message } from 'antd';
import './styleSettingModalGroup.css';
import { useGroupsStore } from '../../../store/groups/Groups';
import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import { Group } from '../../../types/Group';
import DeleteModalGroup from '../deleteGroup/DeleteModalGroup';
import { useAuthStore } from '../../../store/auth/auth';
import { UpdateGroup } from '../../../api/groups/UpdateGroup';
import IconCheck from '../../icons/iconCheck/IconCheck';
import IconClose from '../../icons/iconClose/IconClose';
import IconPlus from '../../icons/iconPus/IconPlus';
import IconDelete from '../../icons/iconDelete/IconDelete';
import IconEdit from '../../icons/iconEdit/IconEdit';
import NewGroupModal2 from '../newGroup2/NewGroupModal2';
import type { TreeProps } from 'antd';

interface SettingGroupModalProps {
    visible: boolean;
    onOk: () => void;
    onCancel: () => void;
    onClose?: () => void;
}

interface TreeNode {
    title: React.ReactNode;
    key: string;
    children?: TreeNode[];
    group: Group;
}

const SettingGroupModal: React.FC<SettingGroupModalProps> = ({ visible, onOk, onCancel, onClose }) => {
    const { groups, fetchGroups } = useGroupsStore();
    const [editingGroup, setEditingGroup] = useState<string | null>(null);
    const [newGroupName, setNewGroupName] = useState<string>('');
    const [newAddGroupName, setAddNewGroupName] = useState<Group>();
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    const [isModalDeleteModalGroup, setIsModalDeleteModalGroup] = useState(false);
    const [isModalNewGroupOpen, setIsModalNewGroupOpen] = useState(false);

    const [selectedGroup, setSelectedGroup] = useState<Group>();
    const { user, SmartDVRToken } = useAuthStore();

    useEffect(() => {
        if (visible) {
            fetchGroups();
        }
    }, [visible]);

    const handleEditGroup = useCallback((group: Group) => {
        setEditingGroup(group?.uid);
        setNewGroupName(group?.name || '');
    }, []);

    const handleSaveGroup = useCallback(async (group: Group) => {
        if (!newGroupName.trim()) {
            message.error('Название группы не может быть пустым');
            return;
        }

        if (!group.uid || !SmartDVRToken || !user) {
            message.error('Ошибка: Не хватает данных для обновления группы');
            return;
        }

        try {
            const response = await UpdateGroup(SmartDVRToken, user.login, {
                uid: group.uid,
                name: newGroupName.trim(),
            });

            if (response && response.success) {
                message.success('Группа успешно обновлена');
                setEditingGroup(null);
                setNewGroupName('');
                fetchGroups();
            } else {
                message.error(`Ошибка обновления: ${response?.error || 'Неизвестная ошибка'}`);
            }
        } catch (error) {
            message.error('Ошибка при обновлении группы');
            console.error('Error updating group:', error);
        }
    }, [newGroupName, SmartDVRToken, user, fetchGroups]);

    const handleCancelEdit = useCallback(() => {
        setEditingGroup(null);
        setNewGroupName('');
    }, []);

    const handleDeleteGroup = useCallback((group: Group) => {
        setEditingGroup(null);
        setIsModalDeleteModalGroup(true);
        setSelectedGroup(group);
    }, []);

    const showModalNewGroup = useCallback((group: Group) => {
        setAddNewGroupName(group);
        setIsModalNewGroupOpen(true);
    }, []);

    const renderTitle = useCallback((entity: Group) =>
        editingGroup === entity.uid ? (
            <div className="editGroup">
                <Input
                    className="input_EG"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onPressEnter={() => handleSaveGroup(entity)}
                    onClick={(e) => e.stopPropagation()}
                />
                <Button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSaveGroup(entity);
                    }}
                    className="button_EG"
                    icon={<IconCheck />}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderColor: '#4D4E65',
                        width: '32px',
                        height: '32px',

                        paddingLeft: '7px',
                        paddingRight: '7px',
                    }}
                />
                <Button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEdit();
                    }}
                    className="button_EG"
                    icon={<IconClose />}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderColor: '#4D4E65',
                        width: '32px',
                        height: '32px',
                        marginRight: '8px',
                        paddingLeft: '7px',
                        paddingRight: '7px',
                    }}
                />
            </div>
        ) : (
            <div className="group-title-container">
                <span className="group-name">{entity.name}</span>
                <div className="group-actions">
                    <Button
                        icon={<IconPlus />}
                        onClick={(e) => {
                            e.stopPropagation();
                            showModalNewGroup(entity);
                        }}
                        style={{ background: '#4D4E65' }}
                        size="small"
                    />
                    <Button
                        icon={<IconEdit />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditGroup(entity);
                        }}
                        style={{ background: '#4D4E65' }}
                        size="small"
                    />
                    <Button
                        icon={<IconDelete />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGroup(entity);
                        }}
                        style={{ background: '#DE7171' }}
                        size="small"
                    />
                </div>
            </div>
        ), [editingGroup, newGroupName, handleSaveGroup, handleCancelEdit, handleEditGroup, handleDeleteGroup, showModalNewGroup]);

    const convertGroupsToTreeData = useCallback((groups: Group[]): TreeNode[] => {
        return groups
            .filter(group =>
                group.uid !== '00000000-0000-0000-0000-000000000003' &&
                group.uid !== '00000000-0000-0000-0000-000000000002'
            )
            .flatMap(group => {
                if (group.name === 'Root' && group.sub_groups) {
                    return convertGroupsToTreeData(group.sub_groups);
                }
                return [{
                    title: renderTitle(group),
                    key: group.uid,
                    group: group,
                    children: group.sub_groups ? convertGroupsToTreeData(group.sub_groups) : undefined
                }];
            });
    }, [renderTitle]);

    const treeData = convertGroupsToTreeData(groups);

    const handleOkDeleteModal = () => {
        setIsModalDeleteModalGroup(false);
        fetchGroups();
    };

    const handleCancelDeleteModal = () => {
        setIsModalDeleteModalGroup(false);
    };

    const handleOkNewGroupModal = () => {
        setIsModalNewGroupOpen(false);
        fetchGroups();
    };

    const handleCancelNewGroupModal = () => {
        setIsModalNewGroupOpen(false);
    };

    const onSelect: TreeProps['onSelect'] = (selectedKeys, info) => {
        console.log('selected', selectedKeys, info);
    };

    return (
        <div>
            <ConfigProvider
                theme={{
                    components: {
                        Modal: {
                            titleColor: '#21201F',
                            titleFontSize: 24,
                            titleLineHeight: 1,
                            paddingMD: 0,
                            padding: 20,
                            paddingContentHorizontalLG: 0,
                        },
                        Tree: {
                            borderRadiusLG: 8,
                        },
                    },
                    token: {
                        borderRadiusLG: 8,
                        fontFamily: 'Roboto',
                    },
                }}
            >
                <Drawer
                    className="modalSettingGroup"
                    visible={visible}
                    onClose={onClose}
                    footer={
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Button className="buttonModalSetting" onClick={onOk}>
                                Сохранить
                            </Button>
                        </div>
                    }
                    width={400}
                    style={{ top: 0, right: 0, margin: 0 }}
                    title={
                        <span className="titleModalSetting">
                            <CloseOutlined
                                className="closeBut"
                                onClick={onCancel}
                                style={{
                                    marginLeft: 'auto',
                                    cursor: 'pointer',
                                }}
                            />
                            Настройка групп
                        </span>
                    }
                    closable={false}
                    bodyStyle={{ padding: 0, height: '100%' }}
                >
                    <div className="menuSetting">
                        <Tree
                            className="groupsTree"
                            treeData={treeData}
                            switcherIcon={<DownOutlined />}
                            expandedKeys={expandedKeys}
                            onExpand={(keys) => setExpandedKeys(keys as string[])}
                            onSelect={onSelect}
                            showIcon={false}
                            blockNode={true}
                        />
                    </div>
                </Drawer>
            </ConfigProvider>
            <DeleteModalGroup
                visible={isModalDeleteModalGroup}
                group={selectedGroup}
                onOk={handleOkDeleteModal}
                onCancel={handleCancelDeleteModal}
            />
            <NewGroupModal2
                visible={isModalNewGroupOpen}
                onOk={handleOkNewGroupModal}
                onCancel={handleCancelNewGroupModal}
                group={newAddGroupName}
            />
        </div>
    );
};

export default SettingGroupModal;
